import React, { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedContent — Batch 7 items 3 & 4.
 *
 * Wraps Axiom-written material (lessons, daily readings, Thought for the day,
 * relapse reading, Relapse Prevention section text) so that a user cannot:
 *   - select the text (CSS user-select: none)
 *   - copy, cut or drag it
 *   - right-click it (context menu blocked)
 *
 * Also lays down a repeating diagonal watermark with the signed-in user's
 * email behind the text (not over it, pointer-events: none, not selectable).
 *
 * Nested inputs / textareas / form fields stay fully usable — only the
 * container reacts to the clipboard events, and we only prevent default
 * when the event's actual target is not an editable element.
 */
export default function ProtectedContent({ children, className = '', ...rest }) {
  const { user } = useAuth();
  const email = (user?.email || '').trim();

  const isEditableTarget = (el) => {
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (el.isContentEditable) return true;
    return false;
  };

  const block = useCallback((e) => {
    if (!isEditableTarget(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Build the watermark SVG data-URI. One tile is drawn with the email
  // rotated -30 degrees; the tile repeats across the full container.
  const watermarkStyle = email
    ? {
        backgroundImage:
          'url("data:image/svg+xml;utf8,' +
          encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="300">` +
              `<g transform="rotate(-30 300 150)" fill="#111111" fill-opacity="0.08" ` +
              `font-family="system-ui, -apple-system, sans-serif" font-size="14" font-weight="400">` +
              `<text x="0" y="60">${email}</text>` +
              `<text x="0" y="160">${email}</text>` +
              `<text x="0" y="260">${email}</text>` +
              `<text x="300" y="110">${email}</text>` +
              `<text x="300" y="210">${email}</text>` +
            `</g>` +
            `</svg>`
          ) +
          '")',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'top left',
      }
    : undefined;

  return (
    <div
      className={`arp-protected relative ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        ...watermarkStyle,
      }}
      onCopy={block}
      onCut={block}
      onContextMenu={block}
      onDragStart={block}
      data-arp-protected="true"
      {...rest}
    >
      {children}
    </div>
  );
}
